import type { DealBookingType, ProductSelectorType } from '@src/types/OTC';
import { SubProductTypeEnum } from '@talos/kyoko';

export type { ProductSelectorType };

export interface ProductConfig {
  value: DealBookingType;
  /** Primary label for this booking type in product lists and detailed UI (not the consolidated Manual Deal "Create …" suffix). */
  displayName: string;
  subProductType: SubProductTypeEnum;
  productTypeDisplayName: string;
  productSubTypeDisplayName: string;
}

export const PRODUCT_CONFIG_MAP = {
  forward: {
    value: 'forward',
    displayName: 'Forward',
    subProductType: SubProductTypeEnum.OTCForward,
    productTypeDisplayName: 'OTC',
    productSubTypeDisplayName: 'Forward',
  },
  fixedRateLoan: {
    value: 'fixedRateLoan',
    displayName: 'Loans - Fixed Rate',
    subProductType: SubProductTypeEnum.OTCFixedRateLoan,
    productTypeDisplayName: 'Loan',
    productSubTypeDisplayName: 'Fixed Rate Loan',
  },
  option: {
    value: 'option',
    displayName: 'Vanilla Option',
    subProductType: SubProductTypeEnum.OTCVanillaOption,
    productTypeDisplayName: 'OTC',
    productSubTypeDisplayName: 'Vanilla Option',
  },
  floatingRateLoan: {
    value: 'floatingRateLoan',
    displayName: 'Loans - Floating Rate',
    subProductType: SubProductTypeEnum.OTCFloatingRateLoan,
    productTypeDisplayName: 'Loan',
    productSubTypeDisplayName: 'Floating Rate Loan',
  },
  repo: {
    value: 'repo',
    displayName: 'Repo',
    //TODO: Placeholder until backend includes the SubProductType value for 'OTCRepo' in the generated types
    subProductType: 'OTCRepo' as SubProductTypeEnum,
    productTypeDisplayName: 'Repo',
    productSubTypeDisplayName: 'Repo',
  },
} as const satisfies Record<DealBookingType, ProductConfig>;

// Options for the Product selector dropdown
// 'repo' is included here but filtered by enableRepoProduct feature flag in DealProductBuilderHeaderSelector
export const PRODUCT_SELECTOR_OPTIONS: { value: ProductSelectorType; label: string }[] = [
  { value: 'otc', label: 'OTC Derivative' },
  { value: 'loan', label: 'Loan' },
  { value: 'repo', label: 'Repo' },
];

// Forward vs vanilla option when OTC is selected (parallel to loan fixed/floating)
export type OtcDerivativeKind = 'forward' | 'option';

export const OTC_DERIVATIVE_OPTIONS: { value: OtcDerivativeKind; displayName: string }[] = [
  { value: 'forward', displayName: 'Forward' },
  { value: 'option', displayName: 'Vanilla Option' },
];

// Loan rate type options for the Fixed/Floating toggle
export type LoanRateType = 'fixed' | 'floating';

export const LOAN_RATE_OPTIONS: { value: LoanRateType; displayName: string }[] = [
  { value: 'fixed', displayName: 'Fixed' },
  { value: 'floating', displayName: 'Floating' },
];

// Legacy: all 4 options for backwards compatibility
export const PRODUCT_OPTIONS = Array.from(Object.values(PRODUCT_CONFIG_MAP)).map(config => ({
  value: config.value,
  label: config.displayName,
}));

export function getProductDisplayName(bookingType: DealBookingType): string {
  return PRODUCT_CONFIG_MAP[bookingType].displayName;
}

/**
 * Short label for the Manual Deal "Create …" control (e.g. "Loan" for both loan rate types,
 * "OTC" for both forward and vanilla option under the OTC bucket).
 */
export function getConsolidatedDisplayName(bookingType: DealBookingType): string {
  switch (bookingType) {
    case 'fixedRateLoan':
    case 'floatingRateLoan':
      return 'Loan';
    case 'forward':
    case 'option':
      return 'OTC';
    case 'repo':
      return 'Repo';
  }
}

/** Map a DealBookingType to the consolidated ProductSelectorType */
export function bookingTypeToSelectorType(bookingType: DealBookingType): ProductSelectorType {
  if (bookingType === 'fixedRateLoan' || bookingType === 'floatingRateLoan') {
    return 'loan';
  }
  if (bookingType === 'repo') {
    return 'repo';
  }
  return 'otc';
}

export type SelectorTypeToBookingTypeOptions = {
  loanRateType?: LoanRateType;
  otcDerivativeKind?: OtcDerivativeKind;
};

/** Map a ProductSelectorType to a DealBookingType */
export function selectorTypeToBookingType(
  selectorType: ProductSelectorType,
  options: SelectorTypeToBookingTypeOptions = {}
): DealBookingType {
  const { loanRateType = 'fixed', otcDerivativeKind = 'forward' } = options;
  if (selectorType === 'loan') {
    return loanRateType === 'floating' ? 'floatingRateLoan' : 'fixedRateLoan';
  }
  if (selectorType === 'repo') {
    return 'repo';
  }
  return otcDerivativeKind === 'option' ? 'option' : 'forward';
}

/** Forward vs option for the OTC sub-toggle; non-option booking types map to `forward`. */
export function getOtcDerivativeKind(bookingType: DealBookingType): OtcDerivativeKind {
  return bookingType === 'option' ? 'option' : 'forward';
}

/** Get the loan rate type from a booking type */
export function getLoanRateType(bookingType: DealBookingType): LoanRateType {
  return bookingType === 'floatingRateLoan' ? 'floating' : 'fixed';
}

/** Get the URL-friendly product type for navigation */
export function getUrlProductType(bookingType: DealBookingType): ProductSelectorType {
  switch (bookingType) {
    case 'fixedRateLoan':
    case 'floatingRateLoan':
      return 'loan';
    case 'forward':
    case 'option':
      return 'otc';
    case 'repo':
      return 'repo';
    default: {
      const _exhaustive: never = bookingType;
      return _exhaustive;
    }
  }
}

/** Query string key: disambiguates `loan` → fixed vs floating, `otc` → forward vs option */
export const DEAL_OTC_DERIVATIVE_QUERY_PARAM = 'dealType' as const;

/** Path segment for the deal product builder route: consolidated bucket only (`otc` | `loan` | `repo`). */
export function isDealUrlBucket(value: string | undefined): value is ProductSelectorType {
  return value === 'otc' || value === 'loan' || value === 'repo';
}

/** Parse `dealType` search param; unknown or missing values yield `undefined`. */
export function parseDealTypeQueryParam(raw: string | null): DealBookingType | undefined {
  switch (raw) {
    case 'forward':
    case 'fixedRateLoan':
    case 'floatingRateLoan':
    case 'option':
    case 'repo':
      return raw;
    default:
      return undefined;
  }
}

/**
 * Resolve {@link DealBookingType} from the deal product builder URL.
 * Path `:bookingType` must be the consolidated bucket (`otc` | `loan`); anything else defaults to `otc`.
 * Optional `?dealType=` disambiguates within the bucket; mismatched values fall back to the bucket default.
 */
export function resolveDealBookingTypeFromUrl(
  pathSegment: string | undefined,
  search: URLSearchParams
): DealBookingType {
  const bucket: ProductSelectorType = isDealUrlBucket(pathSegment) ? pathSegment : 'otc';
  const variant = parseDealTypeQueryParam(search.get(DEAL_OTC_DERIVATIVE_QUERY_PARAM));
  switch (bucket) {
    case 'loan':
      return variant === 'floatingRateLoan' ? 'floatingRateLoan' : 'fixedRateLoan';
    case 'otc':
      return variant === 'option' ? 'option' : 'forward';
    case 'repo':
      return 'repo';
  }
}
